import React, { useState, useContext, useRef, useLayoutEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoNew from "../../images/LogoLAENEW.png";
import UserMenu from "../navbar/UserMenu";
import LanguageSwitcher from "../LanguageSwitcher";
import { UsuarioContext } from "../../providers/userContext";
import { House, Building2, Wrench, Sparkles, ChevronRight, FileText, Briefcase, Lock, LifeBuoy } from "lucide-react";
import ChipNuevo from "../ChipNuevo";
import { houses } from "@lucide/lab";

const MenuLegalia = () => {
  const { t } = useTranslation("nav");
  const user = useContext(UsuarioContext);
  const [navExpanded, setNavExpanded] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const topBarRef = useRef(null);
  const [topBarBottom, setTopBarBottom] = useState(57);

  useLayoutEffect(() => {
    if (!topBarRef.current) return;
    const el = topBarRef.current;
    const measure = () => setTopBarBottom(Math.ceil(el.getBoundingClientRect().bottom));
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  const planName = user.legalia;
  const planLabel = planName === "Elite" ? "Élite" : planName === "Pro" ? "Pro" : "Esencial";
  const planColors = planName === "Elite"
    ? "bg-slate-800 text-white"
    : planName === "Pro"
    ? "bg-pink-100 text-pink-700"
    : "bg-blue-100 text-blue-700";

  const menuItems = [
    { to: "/", icon: <House size={20} strokeWidth={1.5} />, label: t("sidebar.dashboard") },
    { to: "/comunidades", icon: <Building2 size={20} strokeWidth={1.5} />, label: t("sidebar.communities") },
  ];
  if (user?.gruposServicios?.includes(1) || user?.parent === true) {
    menuItems.push({ to: "/proveedores", icon: <Wrench size={20} strokeWidth={1.5} />, label: t("sidebar.suppliers") });
  }
  // Nueva IA Normativa (asistente propio): mismo nombre, con distintivo "Nuevo"
  menuItems.push({ to: "/ia-normativa", icon: <Sparkles size={20} strokeWidth={1.5} />, label: t("sidebar.iaNormativa").trim() });
  // menuItems.push({ to: "/ia-juridica", icon: <Scale size={20} strokeWidth={1.5} />, label: t("sidebar.iaJuridica") });

  const contacts = [];
  if (user?.comerciales) {
    contacts.push({
      icon: <Briefcase size={18} strokeWidth={1.5} />,
      title: t("contacts.commercial"),
      desc: t("contacts.commercialDesc"),
      email: user.comerciales.user?.[0]?.email || user.comerciales.user?.email,
    });
  }
  if (user?.tecnico) {
    contacts.push({
      icon: <Wrench size={18} strokeWidth={1.5} />,
      title: t("contacts.caeTechnician"),
      desc: t("contacts.caeTechnicianDesc"),
      email: user.tecnico.user?.email,
    });
  }
  user?.consultores?.forEach((con) => {
    const isCae = con.r_consul_servsgroups_aaffs?.some((r) => r.group_id === 1);
    const isLopd = con.r_consul_servsgroups_aaffs?.some((r) => r.group_id === 2);
    if (isCae) {
      contacts.push({
        icon: <FileText size={18} strokeWidth={1.5} />,
        title: t("contacts.caeManager"),
        desc: t("contacts.caeManagerDesc"),
        email: con.user?.email,
      });
    }
    if (isLopd) {
      contacts.push({
        icon: <Lock size={18} strokeWidth={1.5} />,
        title: t("contacts.lopdManager"),
        desc: t("contacts.lopdManagerDesc"),
        email: con.user?.email,
      });
    }
  });
  contacts.push({
    icon: <LifeBuoy size={18} strokeWidth={1.5} />,
    title: t("contacts.itSupport"),
    desc: t("contacts.itSupportDesc"),
    email: "soporte.plataforma@grupolae.net",
  });

  const closeNav = () => setNavExpanded(false);

  return (
    <>
      <div ref={topBarRef} className="sticky top-0 z-30 bg-white flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid #e5e7eb" }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setNavExpanded((prev) => !prev)}
            className="bg-transparent border-none cursor-pointer p-1.5"
          >
            <div className="relative" style={{ width: 20, height: 14 }}>
              <span
                className="absolute left-0 w-5 transition-transform duration-200"
                style={{
                  top: "50%",
                  height: 2,
                  marginTop: -1,
                  backgroundColor: "#374151",
                  transformOrigin: "center",
                  transform: navExpanded ? "rotate(45deg)" : "translateY(-5px)",
                }}
              />
              <span
                className="absolute left-0 w-5 transition-opacity duration-200"
                style={{
                  top: "50%",
                  height: 2,
                  marginTop: -1,
                  backgroundColor: "#374151",
                  opacity: navExpanded ? 0 : 1,
                }}
              />
              <span
                className="absolute left-0 w-5 transition-transform duration-200"
                style={{
                  top: "50%",
                  height: 2,
                  marginTop: -1,
                  backgroundColor: "#374151",
                  transformOrigin: "center",
                  transform: navExpanded ? "rotate(-45deg)" : "translateY(5px)",
                }}
              />
            </div>
          </button>
          <img src={LogoNew} alt="GRUPOLAE FINCAS" className="h-10 object-contain" />
        </div>
        <div className="flex items-center">
          <LanguageSwitcher />
          <UserMenu isMobile={false} />
        </div>
      </div>

      {navExpanded && (
        <>
          <div
            className="fixed left-0 right-0 bottom-0 bg-black/30 z-20"
            style={{ top: topBarBottom }}
            onClick={closeNav}
          />
          <div
            className="fixed left-0 right-0 bg-gray-100 shadow-lg overflow-y-auto z-30"
            style={{ top: topBarBottom, maxHeight: `calc(100vh - ${topBarBottom}px)` }}
          >
            {/* Menu items */}
            <nav className="px-4 py-3">
              <ul className="list-none p-0 m-0 flex flex-col gap-1">
                {menuItems.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      onClick={closeNav}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm no-underline whitespace-nowrap [&>svg]:shrink-0 transition-colors duration-150 ${item.nuevo ? "menu-novedad" : ""} ${
                          isActive
                            ? "bg-gray-200 text-gray-900 font-medium"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`
                      }
                    >
                      {item.icon}
                      {item.label}
                      {item.nuevo && <ChipNuevo esquina />}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Plan actual */}
            <div className="px-4 pb-2">
              <div
                style={{
                  position: "relative",
                  overflow: "hidden",
                  background:
                    planName === "Elite"
                      ? "linear-gradient(315deg, rgba(30,41,59,0.15) 0%, #ffffff 60%)"
                      : planName === "Pro"
                      ? "linear-gradient(315deg, rgba(219,39,119,0.12) 0%, #ffffff 60%)"
                      : "linear-gradient(315deg, rgba(59,130,246,0.12) 0%, #ffffff 60%)",
                  borderRadius: 14,
                  padding: "16px 18px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 font-medium">{t("sidebar.currentPlan")}</span>
                  <Sparkles size={16} className="text-gray-400" />
                </div>
                <span className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-semibold ${planColors}`}>
                  {planLabel}
                </span>
              </div>
            </div>

            {/* Contactar */}
            <div className="px-4 pb-4">
              <div
                className="rounded-xl p-3 cursor-pointer transition-colors"
                style={{ border: "1px solid #e5e7eb" }}
                onClick={() => setShowContacts((prev) => !prev)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{t("sidebar.contact")}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{t("sidebar.contactHint")}</div>
                  </div>
                  <ChevronRight size={16} className={`text-gray-400 transition-transform ${showContacts ? "rotate-90" : ""}`} />
                </div>
              </div>

              {showContacts && (
                <div className="mt-2 bg-white rounded-xl border border-gray-200 py-1">
                  {contacts.map((c, i) => (
                    <a
                      key={i}
                      href={`mailto:${c.email}`}
                      className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-gray-50 transition-colors"
                    >
                      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-500 mt-0.5 shrink-0">
                        {c.icon}
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{c.title}</div>
                        <div className="text-xs text-gray-700 mt-0.5 leading-relaxed">{c.desc}</div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MenuLegalia;
